/**
 * Small store behind the report status cards in the top right corner.
 *
 * Report generation is queued on the server and finishes minutes later over
 * SignalR, which is a different shape of message from the app's toasts: it is
 * raised from a service as well as from a page, it changes state in place, and
 * it must not fight the single toast slot the ToastContainer allows.
 */

export type ReportNoticeStatus = "pending" | "queued" | "ready" | "error";

export interface ReportNotice {
  id: number;
  status: ReportNoticeStatus;
  title: string;
  message?: string;
  /** null keeps the card up until it is updated or closed */
  autoCloseMs?: number | null;
}

type Listener = (notices: ReportNotice[]) => void;

let notices: ReportNotice[] = [];
let listeners: Listener[] = [];
let nextId = 1;

const emit = () => {
  const snapshot = notices;
  listeners.forEach((listener) => listener(snapshot));
};

export const subscribeToReportNotices = (listener: Listener) => {
  listeners.push(listener);
  listener(notices);
  return () => {
    listeners = listeners.filter((item) => item !== listener);
  };
};

export const getReportNotices = () => notices;

/** Adds a card and hands back its id so it can be updated in place */
export const pushReportNotice = (notice: Omit<ReportNotice, "id">): number => {
  const id = nextId++;
  notices = [...notices, { ...notice, id }];
  emit();
  return id;
};

export const updateReportNotice = (
  id: number,
  patch: Partial<Omit<ReportNotice, "id">>
) => {
  let didChange = false;
  notices = notices.map((notice) => {
    if (notice.id !== id) {
      return notice;
    }
    didChange = true;
    return { ...notice, ...patch };
  });
  if (didChange) {
    emit();
  }
};

export const dismissReportNotice = (id: number) => {
  const remaining = notices.filter((notice) => notice.id !== id);
  if (remaining.length === notices.length) {
    return;
  }
  notices = remaining;
  emit();
};
